/**
 * Timezone-aware slot computation for the Guidance Conversation booking flow.
 * No date library needed — uses Intl.DateTimeFormat, same technique as
 * formatDualTime in pages/BookConsultation.js.
 */

export const CHRIS_TZ = 'Australia/Brisbane';
/** Fallback template if the Service row has no availableDays/availableHours configured yet. */
export const DEFAULT_AVAILABLE_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export const DEFAULT_AVAILABLE_HOURS = { start: 6, end: 24 };
const MIN_LEAD_HOURS = 24; // give the owner time to see + confirm before the slot arrives
const SEARCH_CAP_DAYS = 60;

/** Disclosed to the customer: the call may come up to this many minutes before the confirmed time. */
export const CALL_LEAD_MINUTES = 5;
/** NOT disclosed — internal reset/overrun buffer reserved after the call, so back-to-back
 *  bookings never sit closer together than a full block (lead + call + trail). */
const CALL_TRAIL_MINUTES = 5;

export const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'Europe/London', label: 'UK (London)' },
  { value: 'Europe/Berlin', label: 'Central Europe (Berlin/Paris)' },
  { value: 'Asia/Kolkata', label: 'India (Mumbai)' },
  { value: 'Australia/Sydney', label: 'Sydney/Melbourne' },
  { value: 'Pacific/Auckland', label: 'New Zealand (Auckland)' },
];

export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

const WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const WEEKDAY_NAME_BY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function getPartsInTz(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const map = {};
  for (const p of parts) map[p.type] = p.value;
  return {
    weekday: WEEKDAY_INDEX[map.weekday],
    hour: Number(map.hour) % 24,
    minute: Number(map.minute),
  };
}

/** True if this instant falls on a configured available day and within the configured hours, in Zee's timezone. */
export function isZeeAvailable(date, durationMinutes, availableDays, availableHours) {
  const days = availableDays && availableDays.length ? availableDays : DEFAULT_AVAILABLE_DAYS;
  const hours = availableHours && typeof availableHours.start === 'number' ? availableHours : DEFAULT_AVAILABLE_HOURS;
  const { weekday, hour, minute } = getPartsInTz(date, CHRIS_TZ);
  if (!days.includes(WEEKDAY_NAME_BY_INDEX[weekday])) return false;
  const hourDecimal = hour + minute / 60;
  return hourDecimal >= hours.start && hourDecimal + durationMinutes / 60 <= hours.end;
}

/** True if this instant's time-of-day in customerTz falls within [startHour, endHour) — supports windows that wrap past midnight. */
export function isWithinCustomerWindow(date, customerTz, startHour, endHour) {
  const { hour, minute } = getPartsInTz(date, customerTz);
  const hourDecimal = hour + minute / 60;
  if (endHour > startHour) {
    return hourDecimal >= startHour && hourDecimal < endHour;
  }
  return hourDecimal >= startHour || hourDecimal < endHour;
}

function roundUpToStep(date, stepMinutes) {
  const ms = stepMinutes * 60 * 1000;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
}

/** Steps forward in durationMinutes increments from `after` until availability (and optionally the customer's window) is satisfied. */
export function findSoonestSlot({ after, durationMinutes, requireCustomerWindow, customerTz, startHour, endHour, availableDays, availableHours }) {
  const stepMs = durationMinutes * 60 * 1000;
  let candidate = roundUpToStep(after, durationMinutes);
  const maxIterations = Math.ceil((SEARCH_CAP_DAYS * 24 * 60) / durationMinutes);
  for (let i = 0; i < maxIterations; i++) {
    if (
      isZeeAvailable(candidate, durationMinutes, availableDays, availableHours) &&
      (!requireCustomerWindow || isWithinCustomerWindow(candidate, customerTz, startHour, endHour))
    ) {
      return candidate;
    }
    candidate = new Date(candidate.getTime() + stepMs);
  }
  return null;
}

/** The actual confirmed/appointment time customers see, given a computed block start (which sits CALL_LEAD_MINUTES earlier). */
export function blockStartToDisplayedTime(blockStart) {
  return new Date(blockStart.getTime() + CALL_LEAD_MINUTES * 60 * 1000);
}

/**
 * Computes the 3 candidate slots (each is a BLOCK START — pass through
 * blockStartToDisplayedTime() to get the time actually shown to the customer):
 * - soonest: earliest bookable time at all
 * - inWindow: earliest bookable time inside the customer's stated preference
 * - laterInWindow: ~3 days after `soonest`, still inside the customer's preference
 *
 * Each booking reserves a full block — CALL_LEAD_MINUTES (may call early, disclosed) +
 * callDurationMinutes (the actual conversation) + CALL_TRAIL_MINUTES (reset/overrun,
 * not disclosed) — so consecutive bookings are always spaced a full block apart.
 *
 * `availableDays`/`availableHours` come from the Service record (Zee's configured hours);
 * falls back to the Mon–Sat/6am–midnight default if not yet configured.
 */
export function computeThreeOptions({ customerTz, startHour, endHour, durationMinutes: callDurationMinutes, availableDays, availableHours }) {
  const blockMinutes = callDurationMinutes + CALL_LEAD_MINUTES + CALL_TRAIL_MINUTES;
  const now = new Date();
  const minStart = new Date(now.getTime() + MIN_LEAD_HOURS * 60 * 60 * 1000);
  const opts = { durationMinutes: blockMinutes, customerTz, startHour, endHour, availableDays, availableHours };

  const soonest = findSoonestSlot({ after: minStart, requireCustomerWindow: false, ...opts });
  if (!soonest) return null;

  const inWindow = findSoonestSlot({ after: minStart, requireCustomerWindow: true, ...opts }) || soonest;

  const threeDaysAfterSoonest = new Date(soonest.getTime() + 3 * 24 * 60 * 60 * 1000);
  const laterInWindow =
    findSoonestSlot({ after: threeDaysAfterSoonest, requireCustomerWindow: true, ...opts }) ||
    findSoonestSlot({ after: new Date(inWindow.getTime() + 3 * 24 * 60 * 60 * 1000), requireCustomerWindow: true, ...opts }) ||
    inWindow;

  return { soonest, inWindow, laterInWindow };
}

/** Dual-timezone display string, e.g. "Wed 22 Jul, 6:00 PM AEST (Wed 22 Jul, 1:00 AM your time)". */
export function formatOptionLabel(date, customerTz) {
  const aest = date.toLocaleString('en-AU', {
    timeZone: CHRIS_TZ,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const local = date.toLocaleString('en-US', {
    timeZone: customerTz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${aest} AEST (${local} your time)`;
}
